/**
 * GameControls renders the control buttons shown during active gameplay:
 * game title, play again (when game ends), info dialog, configure, and close.
 *
 * Why: These controls need consistent styling, focus navigation integration,
 * and conditional visibility based on game state. Extracting them keeps
 * the Header component focused on layout.
 *
 * When to use: Rendered by Header when a game is active (gameId is set).
 * Receives shellVM for game state and actions.
 */
import * as React from "react"
import { cn } from "@merels/ui"
import { useTheme } from "../../../contexts/ThemeContext"
import { useSignals } from '@preact/signals-react/runtime'
import { useControllerButtonHints } from "../../../hooks/useControllerButtonHints"
import { gameControlsTestIds } from "./gameControls.testIds"
import { InfoDialog } from "./InfoDialog"
import { CloseConfirmDialog } from "./CloseConfirmDialog"
import { GameControlButton } from "./GameControlButton"
import { PlayAgainButton } from "./PlayAgainButton"
import { GameTitle } from "./GameTitle"
import { InfoIcon } from "../../library/Icons/InfoIcon"
import { GearIcon } from "../../library/Icons/GearIcon"
import { CloseIcon } from "../../library/Icons/CloseIcon"
import { RulebookIcon } from "../../library/Icons/RulebookIcon"
import { BoardViewIcon } from "../../library/Icons/BoardViewIcon"
import { HintIcon } from "../../library/Icons/HintIcon"
import { HintOffIcon } from "../../library/Icons/HintOffIcon"
import type { ShellVMDep } from "../../../deps/ui/shellVM/shellVM.types"
import type { DevelopmentProgress } from '../../../deps/catalog/developmentProgress.types'
import { DevelopmentStatusBadge } from '../../development/DevelopmentStatus/DevelopmentStatus'

export interface GameControlsProps {
  shellVM: ShellVMDep
  className?: string
  hintsButtonRef?: React.Ref<HTMLButtonElement>
  rulebookButtonRef?: React.Ref<HTMLButtonElement>
  infoButtonRef?: React.Ref<HTMLButtonElement>
  configureButtonRef?: React.Ref<HTMLButtonElement>
  closeButtonRef?: React.Ref<HTMLButtonElement>
  playAgainButtonRef?: React.Ref<HTMLButtonElement>
  focusedItem?: 'hints' | 'rulebook' | 'info' | 'configure' | 'close' | 'play-again' | null
  /** Override app accent (e.g. active turn color). */
  accentColor?: string
  /** Ink on solid accent fill (hover). */
  accentInkColor?: string
  /** Header/backdrop color for focus rings. */
  backgroundColor?: string
  /** Platform-owned participation setup control, placed after Configure. */
  challengeConfigurationControl?: React.ReactNode
  /** Creator-only lifecycle state for the active owned game. */
  developmentProgress?: DevelopmentProgress
  onDevelopmentStatusClick?: () => void
}

export const GameControls = React.forwardRef<HTMLDivElement, GameControlsProps>(
  ({ shellVM, className, hintsButtonRef, rulebookButtonRef, infoButtonRef, configureButtonRef, closeButtonRef, playAgainButtonRef, focusedItem, accentColor: accentColorProp, accentInkColor = '#ffffff', backgroundColor: backgroundColorProp, challengeConfigurationControl, developmentProgress, onDevelopmentStatusClick }, ref) => {
    const { getAppColor, getTitleFont, effectiveTheme } = useTheme()
    useSignals()
    const buttonHints = useControllerButtonHints()
    const appColorShades = getAppColor()
    const accentColor = accentColorProp ?? appColorShades.main
    const backgroundColor = backgroundColorProp ?? appColorShades.background
    const titleFont = getTitleFont()

    const gameName = shellVM.game.gameName.value
    const gameDescription = shellVM.game.gameDescription.value
    const isGameOver = shellVM.game.isGameOver.value
    const hasGameOptions = shellVM.game.hasGameOptions.value
    const hasHints = shellVM.game.hasHints.value
    const hintsEnabled = shellVM.game.hintsEnabled.value
    const hasRulebook = shellVM.game.hasRulebook.value
    const viewMode = shellVM.game.viewMode.value
    const canControlGames = shellVM.room.canControlGames.value

    const infoOpen = shellVM.gameControls.infoDialog.isOpen.value
    const closeConfirmOpen = shellVM.gameControls.closeConfirmDialog.isOpen.value

    const isPlayAgainFocused = focusedItem === 'play-again' && shellVM.focus.isHeaderFocused.value
    const playAgainText = isPlayAgainFocused && buttonHints.select
      ? `Play Again (${buttonHints.select})`
      : 'Play Again'

    // Handle escape key (fallback for keyboard when controller input is not active)
    React.useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
          return
        }
        if (e.key === 'Escape' && (closeConfirmOpen || infoOpen)) {
          return
        }
        const hasOtherDialogsOpen = document.querySelector('[role="dialog"]:not([data-state="closed"])')
        if (e.key === 'Escape' && !infoOpen && !closeConfirmOpen && !hasOtherDialogsOpen) {
          shellVM.gameControls.requestCloseGame()
        }
      }

      window.addEventListener('keydown', handleKeyDown, true)
      return () => window.removeEventListener('keydown', handleKeyDown, true)
    }, [infoOpen, closeConfirmOpen, shellVM])

    if (!gameName) {
      return null
    }

    return (
      <div
        ref={ref}
        className={cn("flex items-center gap-3 min-w-0", className)}
      >
        <GameTitle
          gameName={gameName}
          accentColor={accentColor}
          titleFont={titleFont ?? undefined}
          isDarkMode={effectiveTheme === 'dark'}
        />

        {developmentProgress ? <DevelopmentStatusBadge progress={developmentProgress} onClick={onDevelopmentStatusClick} /> : null}

        {isGameOver && canControlGames && (
          <PlayAgainButton
            buttonRef={playAgainButtonRef}
            onClick={shellVM.actions.playAgain}
            isFocused={isPlayAgainFocused}
            accentColor={accentColor}
            backgroundColor={backgroundColor}
            text={playAgainText}
          />
        )}

        {hasHints && (
          <GameControlButton
            buttonRef={hintsButtonRef}
            icon={
              hintsEnabled
                ? <HintIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />
                : <HintOffIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />
            }
            onClick={shellVM.gameControls.toggleHints}
            ariaLabel={hintsEnabled ? 'Hide hints' : 'Show hints'}
            title={hintsEnabled ? 'Hide hints' : 'Show hints'}
            testId={gameControlsTestIds.hintsButton}
            isFocused={focusedItem === 'hints'}
            accentColor={accentColor}
            backgroundColor={backgroundColor}
            accentInkColor={accentInkColor}
          />
        )}

        {hasRulebook && (
          <GameControlButton
            buttonRef={rulebookButtonRef}
            icon={
              viewMode === 'play'
                ? <RulebookIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />
                : <BoardViewIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />
            }
            onClick={shellVM.gameControls.toggleRulebook}
            ariaLabel={viewMode === 'play' ? 'Open the rulebook' : 'Return to play'}
            title={viewMode === 'play' ? 'Open the rulebook' : 'Return to play'}
            testId={gameControlsTestIds.rulebookButton}
            isFocused={focusedItem === 'rulebook'}
            accentColor={accentColor}
            backgroundColor={backgroundColor}
            accentInkColor={accentInkColor}
          />
        )}

        <InfoDialog
          open={infoOpen}
          onOpenChange={shellVM.gameControls.infoDialog.setIsOpen}
          gameName={gameName}
          gameDescription={gameDescription}
          trigger={
            <GameControlButton
              buttonRef={infoButtonRef}
              icon={<InfoIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />}
              ariaLabel="Game info"
              title="Game info"
              testId={gameControlsTestIds.infoButton}
              isFocused={focusedItem === 'info'}
              accentColor={accentColor}
              backgroundColor={backgroundColor}
            accentInkColor={accentInkColor}
            />
          }
        />

        {hasGameOptions && canControlGames && (
          <GameControlButton
            buttonRef={configureButtonRef}
            icon={<GearIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />}
            onClick={shellVM.gameControls.openConfigure}
            ariaLabel="Configure game"
            title="Configure game"
            isFocused={focusedItem === 'configure'}
            accentColor={accentColor}
            backgroundColor={backgroundColor}
            accentInkColor={accentInkColor}
          />
        )}

        {canControlGames && challengeConfigurationControl}

        {canControlGames && <CloseConfirmDialog
          open={closeConfirmOpen}
          onOpenChange={shellVM.gameControls.closeConfirmDialog.setIsOpen}
          onConfirm={shellVM.gameControls.confirmCloseGame}
          trigger={
            <GameControlButton
              buttonRef={closeButtonRef}
              icon={<CloseIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />}
              onClick={shellVM.gameControls.requestCloseGame}
              ariaLabel="Close the game"
              title="Close the game"
              testId={gameControlsTestIds.closeButton}
              isFocused={focusedItem === 'close'}
              accentColor={accentColor}
              backgroundColor={backgroundColor}
            accentInkColor={accentInkColor}
            />
          }
        />}
      </div>
    )
  }
)
GameControls.displayName = "GameControls"
